import React, { useState, useEffect } from 'react';
import {
  Users, TrendingUp, TrendingDown, Clock, CheckCircle, Star,
  AlertTriangle, Target, Activity, BarChart3, Filter, Search,
  RefreshCw, Download, Calendar, Award, Zap, Timer
} from 'lucide-react';
import { AdminService } from '../../services/AdminService';
import { Link } from 'react-router-dom';

/** Mock AdminService KPIs must not be shown as production reporting (B3). */
const PRODUCTION_KPI_REPORT_ENABLED = false;

const AgentKPIsUnavailable = () => (
  <div className="p-6">
    <div className="max-w-2xl rounded-lg border border-amber-200 bg-amber-50 p-6 dark:border-amber-800 dark:bg-amber-950/30">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-6 w-6 shrink-0 text-amber-600" />
        <div>
          <h1 className="text-xl font-semibold text-foreground">Agent KPIs — not available</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            This screen is not connected to live analytics. FCR and first-response KPIs remain deferred pending product decisions D-21 and D-22.
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            For production agent metrics, use{' '}
            <Link to="/admin/reports/agents" className="font-medium text-primary underline-offset-2 hover:underline">
              Agents Report
            </Link>{' '}
            or{' '}
            <Link to="/admin/reports/agent-wise-sla-breach" className="font-medium text-primary underline-offset-2 hover:underline">
              Agent Wise SLA Breach
            </Link>
            .
          </p>
        </div>
      </div>
    </div>
  </div>
);

const AgentKPIsLegacy = () => {
  const [kpiData, setKpiData] = useState(null);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState('all');
  const [selectedPeriod, setSelectedPeriod] = useState('30d');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadAgentKPIs();
  }, [selectedPeriod, selectedAgent]);

  const loadAgentKPIs = async () => {
    try {
      setLoading(true);
      const response = await new AdminService().getAgentKPIs(selectedPeriod, selectedAgent);
      if (response.success) {
        setKpiData(response.data);
        if (response.data.agents) {
          setAgents(response.data.agents);
        }
      } else {
        setError(response.message || 'Failed to load Agent KPIs');
      }
    } catch (err) {
      setError('Failed to load Agent KPIs');
      console.error('Error loading Agent KPIs:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredAgents = agents.filter(agent => {
    const matchesSearch = agent.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         agent.email?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  const getTrendIcon = (trend) => {
    if (trend === 'up') return <TrendingUp className="h-4 w-4 text-green-600" />;
    if (trend === 'down') return <TrendingDown className="h-4 w-4 text-red-600" />;
    return <Activity className="h-4 w-4 text-gray-600" />;
  };

  const getPerformanceColor = (value, target, higherIsBetter = true) => {
    const percentage = (value / target) * 100;
    if (higherIsBetter) {
      if (percentage >= 100) return 'text-green-600';
      if (percentage >= 80) return 'text-yellow-600';
      return 'text-red-600';
    } else {
      if (percentage <= 100) return 'text-green-600';
      if (percentage <= 120) return 'text-yellow-600';
      return 'text-red-600';
    }
  };

  if (loading && !kpiData) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <RefreshCw className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-2" />
            <p className="text-gray-600">Loading Agent KPIs...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-red-600 mr-2" />
            <p className="text-red-800">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Agent KPIs</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Track and analyze agent performance metrics</p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => {/* Export functionality */}}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <Download className="h-4 w-4" />
            <span>Export</span>
          </button>
          <button
            onClick={loadAgentKPIs}
            className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <RefreshCw className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center space-x-4 bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search agents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
            />
          </div>
        </div>
        <select
          value={selectedAgent}
          onChange={(e) => setSelectedAgent(e.target.value)}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
        >
          <option value="all">All Agents</option>
          {agents.map(agent => (
            <option key={agent.id} value={agent.id}>{agent.name}</option>
          ))}
        </select>
        <select
          value={selectedPeriod}
          onChange={(e) => setSelectedPeriod(e.target.value)}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
        >
          <option value="7d">Last 7 Days</option>
          <option value="30d">Last 30 Days</option>
          <option value="90d">Last 90 Days</option>
          <option value="custom">Custom Range</option>
        </select>
      </div>

      {/* Overall KPIs */}
      {kpiData?.overview && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              {getTrendIcon(kpiData.overview.totalTickets?.trend)}
            </div>
            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Tickets Resolved</h3>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">
              {kpiData.overview.totalTickets?.value || 0}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {kpiData.overview.totalTickets?.change || '0%'} from last period
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                <Timer className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              {getTrendIcon(kpiData.overview.avgResolutionTime?.trend)}
            </div>
            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Avg Resolution Time</h3>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">
              {kpiData.overview.avgResolutionTime?.value || '0h'}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {kpiData.overview.avgResolutionTime?.change || '0%'} from last period
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
                <Star className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
              </div>
              {getTrendIcon(kpiData.overview.customerSatisfaction?.trend)}
            </div>
            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Customer Satisfaction</h3>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">
              {kpiData.overview.customerSatisfaction?.value || '0.0'}/5.0
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {kpiData.overview.customerSatisfaction?.change || '0%'} from last period
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                <Target className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
              {getTrendIcon(kpiData.overview.slaCompliance?.trend)}
            </div>
            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">SLA Compliance</h3>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">
              {kpiData.overview.slaCompliance?.value || '0'}%
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {kpiData.overview.slaCompliance?.change || '0%'} from last period
            </p>
          </div>
        </div>
      )}

      {/* Agent Performance Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Agent Performance</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Agent</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Tickets Resolved</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Avg Resolution</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">First Response</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Satisfaction</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">SLA Compliance</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">FCR Rate</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Productivity</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredAgents.map((agent) => (
                <tr key={agent.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                        <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{agent.name}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{agent.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-gray-100">{agent.metrics?.ticketsResolved || 0}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-gray-100">{agent.metrics?.avgResolutionTime || '0h'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-gray-100">{agent.metrics?.firstResponseTime || '0m'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Star className="h-4 w-4 text-yellow-400 mr-1" />
                      <span className="text-sm text-gray-900 dark:text-gray-100">{agent.metrics?.satisfaction || '0.0'}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`text-sm font-medium ${getPerformanceColor(agent.metrics?.slaCompliance || 0, 95)}`}>
                      {agent.metrics?.slaCompliance || 0}%
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900 dark:text-gray-100">{agent.metrics?.firstCallResolution || 0}%</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {getTrendIcon(agent.metrics?.productivityTrend)}
                      <span className="text-sm text-gray-900 dark:text-gray-100 ml-1">
                        {agent.metrics?.productivityScore || 0}%
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredAgents.length === 0 && (
          <div className="text-center py-12">
            <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">No agents found</p>
          </div>
        )}
      </div>
    </div>
  );
};

const AgentKPIs = () => (PRODUCTION_KPI_REPORT_ENABLED ? <AgentKPIsLegacy /> : <AgentKPIsUnavailable />);

export default AgentKPIs;

